import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { trpc } from "../lib/api/trpc";

/**
 * Page for email verification (both new registrations and email changes)
 */
const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [message, setMessage] = useState<string>("");
  const [isEmailChange, setIsEmailChange] = useState<boolean>(false);

  // Ref to prevent multiple verification attempts (handles StrictMode double-mounting)
  const verificationAttemptedRef = useRef(false);

  // Get token and type from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const type = searchParams.get("type"); // 'change' for email changes, null/undefined for regular verification

  // tRPC mutation for email verification
  // @ts-ignore - tRPC type issues
  const verifyEmailMutation = trpc.auth.verifyEmail.useMutation();

  useEffect(() => {
    // Prevent multiple executions using ref (handles React StrictMode double-mount)
    if (verificationAttemptedRef.current) {
      return;
    }
    verificationAttemptedRef.current = true;

    const performVerification = async () => {
      if (!token) {
        setVerificationStatus("error");
        setMessage("No verification token provided");
        return;
      }

      try {
        setIsEmailChange(type === "change");
        console.log(
          "Starting email verification with token:",
          token,
          "type:",
          type,
        );

        // @ts-ignore - Type checking for tRPC mutation
        const result = await verifyEmailMutation.mutateAsync({ token });
        console.log("Email verification result:", result);

        setVerificationStatus("success");
        if (type === "change") {
          setMessage("Your email address has been successfully updated!");
        } else {
          setMessage("Your email address has been successfully verified!");
        }
      } catch (error: any) {
        console.error("Email verification failed:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        setVerificationStatus("error");
        setMessage(
          error?.message ||
            "Email verification failed. The token may be invalid or expired.",
        );
      }
    };

    performVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, type]);

  const handleGoToDashboard = () => {
    navigate("/");
  };

  const handleGoToLogin = () => {
    navigate("/auth/login");
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%", textAlign: "center" }}>
          <Typography component="h1" variant="h4" gutterBottom>
            Email Verification
          </Typography>

          {verificationStatus === "loading" && (
            <Box sx={{ my: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Verifying your email address...
              </Typography>
            </Box>
          )}

          {verificationStatus === "success" && (
            <Box sx={{ my: 4 }}>
              <CheckCircleIcon
                sx={{ fontSize: 80, color: "success.main", mb: 2 }}
              />
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              {isEmailChange ? (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Your email address has been successfully updated!
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Important:</strong> Since your email address has
                    changed, please log back in with your new email address to
                    ensure your session is properly updated.
                  </Alert>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Your account is now active and you can log in with your
                  credentials.
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                {isEmailChange ? (
                  <Button
                    variant="contained"
                    onClick={handleGoToLogin}
                    size="large"
                  >
                    Login with New Email
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleGoToDashboard}
                      size="large"
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleGoToLogin}
                      size="large"
                    >
                      Login
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          )}

          {verificationStatus === "error" && (
            <Box sx={{ my: 4 }}>
              <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
              <Alert severity="error" sx={{ mb: 3 }}>
                {message}
              </Alert>
              <Typography variant="body1" sx={{ mb: 3 }}>
                This verification link may have expired or already been used.
                Please try requesting a new verification email if needed.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleGoToLogin}
                  size="large"
                >
                  Go to Login
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/")}
                  size="large"
                >
                  Home
                </Button>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {isEmailChange
                ? "This email change verification was sent to your new email address."
                : "This verification email was sent when your account was created."}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;
